// messageSlice.js
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { API_BASE_URL } from "../config";
import { setError } from "./errorSlice";

// POST: Reply to a message
export const replyToMessage = createAsyncThunk(
  "message/reply",
  async ({ messageId, replyData }, { rejectWithValue, dispatch }) => {
    try {
      const storedUser = localStorage.getItem("userInfo");
      if (!storedUser) {
        dispatch(setError("Session expired. Please log in again."));
        return rejectWithValue("Session expired. Please log in again.");
      }

      const { token } = JSON.parse(storedUser);

      const response = await axios.post(
        `${API_BASE_URL}/api/Communication/Message/Reply/${messageId}`,
        replyData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
          error.message ||
          "Failed to reply to message"
      );
    }
  }
);

// POST: Send a message
export const sendMessage = createAsyncThunk(
  "message/send",
  async (messageData, { rejectWithValue, dispatch }) => {
    try {
      const storedUser = localStorage.getItem("userInfo");
      if (!storedUser) {
        dispatch(setError("Session expired. Please log in again."));
        return rejectWithValue("Session expired. Please log in again.");
      }

      const { token } = JSON.parse(storedUser);
      if (!token) {
        dispatch(setError("Session expired. Please log in again."));
        return rejectWithValue("Session expired. Please log in again.");
      }

      const payload = {
        senderId: messageData.senderId,
        receiverId: messageData.receiverId || null,
        groupId: messageData.groupId || null,
        content: messageData.content,
        contentAttachment: messageData.contentAttachment,
        attachmentType: messageData.attachmentType,
        isReplyAllowed: messageData.isReplyAllowed,
        isEmoj: messageData.isEmoj,
        replied_To_Id: messageData.replied_To_Id,
      };

      const response = await axios.post(
        `${API_BASE_URL}/api/Communication/Message`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data?.success) {
        return response.data.message;
      } else {
        throw new Error("Message sending failed.");
      }
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
          error.message ||
          "Failed to send message"
      );
    }
  }
);

// ✅ NEW: GET group messages
export const getGroupMessages = createAsyncThunk(
  "message/fetchGroup",
  async (groupId, { rejectWithValue, dispatch }) => {
    try {
      const storedUser = localStorage.getItem("userInfo");
      if (!storedUser) {
        dispatch(setError("Session expired. Please log in again."));
        return rejectWithValue("Session expired. Please log in again.");
      }

      const { token } = JSON.parse(storedUser);
      if (!token) {
        dispatch(setError("Session expired. Please log in again."));
        return rejectWithValue("Session expired. Please log in again.");
      }

      const response = await axios.get(
        `${API_BASE_URL}/api/Communication/Message/Group/${groupId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
          error.message ||
          "Failed to fetch group messages"
      );
    }
  }
);

// ✅ NEW: GET user messages
export const getUserMessages = createAsyncThunk(
  "message/fetchUser",
  async (userId, { rejectWithValue, dispatch }) => {
    try {
      const storedUser = localStorage.getItem("userInfo");
      if (!storedUser) {
        dispatch(setError("Session expired. Please log in again."));
        return rejectWithValue("Session expired. Please log in again.");
      }

      const { token } = JSON.parse(storedUser);
      if (!token) {
        dispatch(setError("Session expired. Please log in again."));
        return rejectWithValue("Session expired. Please log in again.");
      }

      const response = await axios.get(
        `${API_BASE_URL}/api/Communication/Message/User/${userId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
          error.message ||
          "Failed to fetch user messages"
      );
    }
  }
);

// NEW: PUT - Update/Edit Message
export const updateMessage = createAsyncThunk(
  "message/update",
  async ({ id, updatedData }, { rejectWithValue, dispatch }) => {
    try {
      const storedUser = localStorage.getItem("userInfo");
      if (!storedUser) {
        dispatch(setError("Session expired. Please log in again."));
        return rejectWithValue("Session expired. Please log in again.");
      }

      const { token } = JSON.parse(storedUser);

      const response = await axios.put(
        `${API_BASE_URL}/api/Communication/Message/${id}`,
        updatedData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
          error.message ||
          "Failed to update message"
      );
    }
  }
);

// NEW: DELETE - Delete Message
export const deleteMessage = createAsyncThunk(
  "message/delete",
  async (id, { rejectWithValue, dispatch }) => {
    try {
      const storedUser = localStorage.getItem("userInfo");
      if (!storedUser) {
        dispatch(setError("Session expired. Please log in again."));
        return rejectWithValue("Session expired. Please log in again.");
      }

      const { token } = JSON.parse(storedUser);

      await axios.delete(`${API_BASE_URL}/api/Communication/Message/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      return id;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
          error.message ||
          "Failed to delete message"
      );
    }
  }
);

const messageSlice = createSlice({
  name: "message",
  initialState: {
    groupMessages: [],
    privateMessages: [],
    loading: false,
    error: null,
  },
  reducers: {
    clearMessages(state) {
      state.messages = [];
    },
  },
  extraReducers: (builder) => {
    builder

      .addCase(replyToMessage.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(replyToMessage.fulfilled, (state, action) => {
        state.loading = false;
        const repliedMessage = action.payload;
        if (repliedMessage?.groupId) {
          state.groupMessages.push(repliedMessage);
        } else {
          state.privateMessages.push(repliedMessage);
        }
      })
      .addCase(replyToMessage.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to reply to message";
      })

      // Send Message
      .addCase(sendMessage.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(sendMessage.fulfilled, (state, action) => {
        state.loading = false;
        if (Array.isArray(action.payload)) {
          state.messages = [...state.messages, ...action.payload];
        } else {
          state.messages.push(action.payload);
        }
      })
      .addCase(sendMessage.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload ||
          "An unknown error occurred while sending the message";
      })

      // ✅ Fetch Group Messages
      .addCase(getGroupMessages.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getGroupMessages.fulfilled, (state, action) => {
        state.loading = false;
        state.groupMessages = action.payload || [];
      })
      .addCase(getGroupMessages.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to fetch group messages";
      })
      // ✅ Update Message
      .addCase(updateMessage.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateMessage.fulfilled, (state, action) => {
        state.loading = false;
        const updated = action.payload;
        const updateIn = [...state.groupMessages, ...state.privateMessages];
        const index = updateIn.findIndex((msg) => msg.id === updated.id);
        if (index !== -1) {
          updateIn[index] = updated;
        }
      })
      .addCase(updateMessage.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to update message";
      })

      // ✅ Delete Message
      .addCase(deleteMessage.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteMessage.fulfilled, (state, action) => {
        state.loading = false;
        const deletedId = action.payload;
        state.groupMessages = state.groupMessages.filter(
          (msg) => msg.id !== deletedId
        );
        state.privateMessages = state.privateMessages.filter(
          (msg) => msg.id !== deletedId
        );
      })
      .addCase(deleteMessage.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to delete message";
      })

      // ✅ Fetch User Messages
      .addCase(getUserMessages.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getUserMessages.fulfilled, (state, action) => {
        state.loading = false;
        state.privateMessages = action.payload || [];
      })
      .addCase(getUserMessages.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to fetch user messages";
      });
  },
});

export const { clearMessages } = messageSlice.actions;
export default messageSlice.reducer;
